import React from "react";
import { useSelector } from "react-redux";

// components
import ParticipatingUniversitiesTab from "./universitiesTab";
import Loader from "./loader";

// utils
import { retriveFromSession } from "@/utils/session";

// mui
import { Box, makeStyles, Typography } from "@material-ui/core";
const useStyles = makeStyles((theme) => ({
    buttonWrap: {
        display: "flex",
        gap: theme.spacing(4),
        [theme.breakpoints.down("sm")]: {
            gap: theme.spacing(1),
        },
    },
    button: {
        fontSize: "16px",
        fontWeight: 500,
        cursor: "pointer",
        padding: theme.spacing(1, 2),
        color: theme.palette.text.disabled,
        background: theme.palette.common.white,
        position: "relative",
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1, 1.5),
            fontSize: "12px",
        },
    },
    outline: {
        height: "100%",
        position: "absolute",
        width: "100%",
        left: 0,
        top: 0,
        border: `2px solid ${theme.palette.primary.main}`,
        borderBottomColor: "white",
    },
    activeButton: {
        color: `${theme.palette.primary.main}!important`,
        fontWeight: 600,
        marginBottom: "-2px",
        paddingBottom: "10px",
    },
    tabPanel: {
        border: `2px solid ${theme.palette.primary.main}`,
    },
}));

export default function UniversityTab() {
    const classes = useStyles();
    const [value, setValue] = React.useState(0);
    const prevCodesRef = React.useRef([]);
    const handleChange = (_, newValue) => {
        setValue(newValue);
    };
    const registrantCodes = (retriveFromSession("registrantCode") || "")
        .split(",")
        .map((code) => code.trim())
        .filter(Boolean);
    const loadingStudentList = useSelector((state) => state.studentDetails.loadingList);
    const data = (useSelector((state) => state.studentDetails.data) || []).filter((student) =>
        registrantCodes.includes(`${student.registrantCode}`)
    );
    const codesKey = data.map((student) => `${student.registrantCode}`).join(",");

    // After a moved student is removed, keep an adjacent remaining tab selected
    React.useEffect(() => {
        const codes = codesKey ? codesKey.split(",") : [];
        const prevCodes = prevCodesRef.current;
        const prevSelectedCode = prevCodes[value];
        const stillIdx = codes.indexOf(prevSelectedCode);

        if (!codes.length) {
            setValue(0);
        } else if (stillIdx >= 0) {
            setValue(stillIdx);
        } else {
            // Prefer next (same index after removal), else previous
            setValue(Math.max(0, Math.min(value, codes.length - 1)));
        }

        prevCodesRef.current = codes;
    }, [codesKey]);

    if (loadingStudentList) {
        return <Loader />;
    }

    const selectedIndex = data.length ? Math.min(value, data.length - 1) : 0;

    return (
        <Box>
            <Box className={classes.buttonWrap}>
                {data.map((student, index) => (
                    <Typography
                        key={student.registrantCode}
                        className={`${classes.button} ${
                            selectedIndex === index ? classes.activeButton : ""
                        }`}
                        onClick={() => handleChange(null, index)}
                    >
                        <span className={selectedIndex === index ? classes.outline : ""}></span>
                        {student.registrantCode}
                    </Typography>
                ))}
            </Box>
            {data.map((student, index) => (
                <div
                    key={student.registrantCode}
                    hidden={selectedIndex !== index}
                    role="tabpanel"
                    className={classes.tabPanel}
                >
                    <ParticipatingUniversitiesTab
                        participatingUniversities={student.participatingUniversities}
                        registrantCode={student.registrantCode}
                        firstName={student.firstName}
                        lastName={student.lastName}
                        allNotes={student.allNotes || []}
                    />
                </div>
            ))}
        </Box>
    );
}
